import React, { useRef, useEffect, useState } from "react";
import isEqual from "lodash.isequal";
import ProductListButton from "../../../Button/ProductListButton";

export const getDisplayLabel = (val, field, dropdownData) => {
  if (!val) return "—"; // Show dash for empty values

  // If val is populated object
  if (typeof val === "object") {
    return (
      val?.name ||
      val?.userId ||
      val?.itemCode || // for BOQ
      val?.projectName || // for Project
      val?._id || // fallback to ID
      "Unnamed"
    );
  }

  // If val is ID, look up in dropdownData
  return (
    dropdownData?.[field]?.find((opt) => opt.id === val)?.label ||
    val ||
    "Unnamed"
  );
};

export const updatePendingChanges = (productId, fieldName, newValue, setPendingChanges) => {
  setPendingChanges((prev) => ({
    ...prev,
    [productId]: {
      ...(prev[productId] || {}),
      _id: productId,
      [fieldName]: newValue,
    },
  }));
};




export const handleDoubleClick = (
  field,
  value,
  isEditable,
  productId,
  handleCellClick,
  setEditingCell
) => {
  if (!isEditable) return;

  const isPopupOnly = field.editPopUp === "true" && !!field.popupCalling;

  if (isPopupOnly) {
    setEditingCell({
      rowId: productId,
      fieldName: field.fields,
      showPopup: true,
      popupMode: "select",
      popupEntryId: null,
    });
  } else {
    setEditingCell({ rowId: productId, fieldName: field.fields });
    handleCellClick?.(productId, field.fields, value);
  }
};




export const calculateFieldTotals = (fields, filteredProducts) => {
  const totals = {};

  fields.forEach((field) => {
    if (field.fields === "serialNo" || field.fields === "srNo") {
      totals[field.fields] = filteredProducts.length;
    } else if (field.type === "number") {
      totals[field.fields] = filteredProducts.reduce((sum, item) => {
        const val = parseFloat(item[field.fields]);
        return sum + (isNaN(val) ? 0 : val);
      }, 0);
    } else if (field.type === "numberparts") {
      let totalReceived = 0;
      let totalExpected = 0;

      filteredProducts.forEach((item) => {
        const numberPartsValue = item?.[field.fields];
        if (
          typeof numberPartsValue === "string" &&
          numberPartsValue.includes("/")
        ) {
          const [received, expected] = numberPartsValue.split("/").map(Number);
          if (!isNaN(received)) totalReceived += received;
          if (!isNaN(expected)) totalExpected += expected;
        }
      });

      totals[field.fields] = `${totalReceived}/${totalExpected}`;
    }
  });

  return totals;
};

export const canEditRow = (product, user, roleCompare = []) => {
  if (!user || !product) return false;

  const currentUserId = user._id;

  // Normalize user's role names
  const roleNames = (user.roles || []).map((r) =>
    typeof r === "object" ? r.roleName : r
  );

  // ✅ Allow Super Admin and Admin Master
  if (roleNames.includes("Super Admin") || roleNames.includes("Admin Master")) {
    return true;
  }

  // Loop through roleCompare rules
  for (const rule of roleCompare) {
    const { field, Role } = rule;

    const fieldValue = product[field];

    if (!fieldValue) continue; // skip if field not present

    const matchesRole = Role === "any" || roleNames.includes(Role);
    if (!matchesRole) continue;

    const fieldIds = Array.isArray(fieldValue)
      ? fieldValue.map((entry) =>
          typeof entry === "object" ? entry._id : entry
        )
      : [typeof fieldValue === "object" ? fieldValue._id : fieldValue];

    if (fieldIds.includes(currentUserId)) {
      return true;
    }
  }

  return false;
};

export const FileUploadButton = ({ fieldName, productId, onUploadSuccess }) => {
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert("File size should be less than 5MB");
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(
        "https://rohan-track-backend.vercel.app/billing/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      if (result.viewLink) {
        onUploadSuccess(productId, fieldName, result.viewLink);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      alert("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="file-upload-container">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        id={`file-upload-${productId}-${fieldName}`}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" // Specify accepted file types
      />
      <label
        htmlFor={`file-upload-${productId}-${fieldName}`}
        className="file-upload-button"
        title="Upload file"
      >
        {isUploading ? (
          <span>
            <i className="fas fa-spinner fa-spin"></i> Uploading
          </span>
        ) : (
          <span>
            <i className="fas fa-upload"></i>{" "}
          </span>
        )}
      </label>
    </div>
  );
};

export const NumberPartsEditor = ({
  product,
  field,
  editedValue,
  setEditedValue,
  setPendingChanges,
  setEditingCell,
  currentProducts,
}) => {
  const initialParts = (editedValue || "").split("/");
  const [received, setReceived] = useState(initialParts[0] || "");
  const [total, setTotal] = useState(initialParts[1] || "");

  const handleSave = () => {
    const finalValue = `${received}/${total}`;
    const updated = [...currentProducts];
    const index = updated.findIndex((p) => p._id === product._id);
    if (index !== -1) {
      updated[index][field.fields] = finalValue;
      setPendingChanges((prev) => ({
        ...prev,
        [product._id]: {
          ...(prev[product._id] || {}),
          [field.fields]: finalValue,
        },
      }));
    }
    setEditedValue(finalValue);
    setEditingCell(null);
  };

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <input
        type="number"
        value={received}
        onChange={(e) => setReceived(e.target.value)}
        placeholder="0"
        style={{
          width: "40px",
          textAlign: "center",
          padding: "2px 4px",
          fontSize: "14px",
        }}
        autoFocus
      />
      <span style={{ margin: "0 4px", fontSize: "16px", fontWeight: "bold" }}>
        /
      </span>
      <input
        type="number"
        value={total}
        onChange={(e) => setTotal(e.target.value)}
        placeholder="0"
        style={{
          width: "40px",
          textAlign: "center",
          padding: "2px 4px",
          fontSize: "14px",
        }}
      />
      <button
        onClick={handleSave}
        style={{
          marginLeft: "8px",
          backgroundColor: "#4CAF50",
          color: "white",
          border: "none",
          padding: "2px 8px",
          cursor: "pointer",
          fontSize: "12px",
          borderRadius: "4px",
          transition: "background-color 0.2s ease",
        }}
      >
        ✔
      </button>
    </div>
  );
};

export const SaveChangesButton = ({ onSave }) => {
  return (
    <ProductListButton
      onClick={onSave}
      icon="fas fa-save"
      label="Save Changes"
    />
  );
};
